import React, { useState } from 'react';
import { Checkbox } from '@/core/Checkbox.atom';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import TimeZoneSelector from '@/core/TimeZoneSelector.molecule';
import { setUserPreferencesDisplay, setUserPreferencesTab } from '@/workbench/workbench.actions';
import _ from 'lodash';
import { sqWorksheetStore } from '@/core/core.stores';
import { setTimezone as worksheetActionsSetTimezone } from '@/worksheet/worksheet.actions';
import { Modal } from '@seeqdev/qomponents';
import { PREFERENCE_TABS } from '@/workbench/workbench.store';

interface TimezoneModalProps {
  close: () => void;
  userFallback: boolean;
  onChange?: (timezone?: { name: string }) => void;
}

export const TimezoneModal: React.FunctionComponent<TimezoneModalProps> = ({
  close,
  onChange = _.noop,
  userFallback,
}) => {
  const { t } = useTranslation();

  const [fixed, setFixed] = useState(sqWorksheetStore.timezoneFixed);
  const [timezone, setTimezone] = useState(sqWorksheetStore.timezone);

  const openUserPreferences = () => {
    setUserPreferencesTab(PREFERENCE_TABS.SYSTEM);
    setUserPreferencesDisplay(true);
    close();
  };

  const save = () => {
    if (fixed !== sqWorksheetStore.timezoneFixed || timezone !== sqWorksheetStore.timezone) {
      worksheetActionsSetTimezone(fixed ? timezone : undefined);
      onChange(fixed ? timezone : undefined);
    }
    close();
  };

  return (
    <Modal
      open={true}
      onClose={close}
      size="sm"
      title={t('TIME_ZONE_MODAL.TITLE')}
      cancelButtonLabel={t('CANCEL')}
      submitButtonLabel={t('SAVE')}
      onSubmit={save}>
      <div>
        <div className="mb15">
          {t(userFallback ? 'TIME_ZONE_MODAL.DESCRIPTION_WORKSHEET' : 'TIME_ZONE_MODAL.DESCRIPTION_TOPIC')}
        </div>
        <div className="flexRowContainer mb0">
          <div id="timezoneLocal" className="flexColumnContainer flexSpaceBetween flexAlignCenter">
            <Checkbox
              type="radio"
              id="setTimeZoneFlex"
              isChecked={!fixed}
              onClick={() => setFixed(false)}
              tooltip={userFallback ? 'TIME_ZONE_MODAL.USER_TOOLTIP' : 'TIME_ZONE_MODAL.NOT_CONFIGURED_TOOLTIP'}
              label={userFallback ? 'TIME_ZONE_MODAL.USER' : 'TIME_ZONE_MODAL.NOT_CONFIGURED'}
            />
            {userFallback && (
              <HoverTooltip text="TIME_ZONE_MODAL.EDIT_PREFERENCES_TOOLTIP" placement="right">
                <a
                  type="button"
                  className="ml5 mr8 cursorPointer font-size-smaller text-italic sq-text-primary text-underline"
                  onClick={openUserPreferences}>
                  {t('TIME_ZONE_MODAL.EDIT_PREFERENCES')}
                </a>
              </HoverTooltip>
            )}
          </div>
          <div id="timezoneWorksheet" className="flexRowContainer mt2" onClick={() => setFixed(true)}>
            <Checkbox
              type="radio"
              id="setTimeZoneFixed"
              isChecked={fixed}
              onClick={() => setFixed(true)}
              tooltip={userFallback ? 'TIME_ZONE_MODAL.FIXED_TOOLTIP' : 'TIME_ZONE_MODAL.FIXED_TOPIC_TOOLTIP'}
              label="TIME_ZONE_MODAL.FIXED"
            />
            <HoverTooltip
              placement="right"
              text={userFallback ? 'TIME_ZONE_MODAL.FIXED_TOOLTIP' : 'TIME_ZONE_MODAL.FIXED_TOPIC_TOOLTIP'}>
              <div className="ml25">
                <TimeZoneSelector timezone={timezone} onSelect={setTimezone} />
              </div>
            </HoverTooltip>
          </div>
        </div>
      </div>
    </Modal>
  );
};
